import React from 'react';
import { AlertTriangle, RefreshCw, Wifi, WifiOff } from 'lucide-react';
import { Button } from '@/components/ui/button';

// ===== Global Error Boundary =====
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo);
    this.setState({ errorInfo });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      const errorMessage = this.state.error?.message || String(this.state.error || 'Unknown error');
      const errorStack = this.state.error?.stack || '';
      const componentStack = this.state.errorInfo?.componentStack || '';
      return (
        <div className="min-h-screen flex items-start justify-center bg-background p-4 sm:p-8 overflow-auto" data-testid="error-boundary">
          <div className="text-center max-w-2xl w-full space-y-4 pt-8">
            <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto" />
            <h2 className="text-xl font-semibold text-foreground">Something went wrong</h2>
            <p className="text-sm text-muted-foreground">
              The application encountered an unexpected error. Please try again.
            </p>
            <div className="flex gap-3 justify-center">
              <Button onClick={this.handleRetry} variant="outline" data-testid="error-retry-btn">
                <RefreshCw className="h-4 w-4 mr-2" /> Try Again
              </Button>
              <Button onClick={() => window.location.href = '/login'} data-testid="error-home-btn">
                Go to Login
              </Button>
            </div>
            {/* Inline diagnostic — collapsed by default, expand to read the error.
                Lets you see the actual error without opening DevTools. */}
            <details className="text-left bg-slate-50 border border-slate-200 rounded-lg p-3 mt-6" data-testid="error-details">
              <summary className="text-xs font-medium text-slate-700 cursor-pointer">
                Show error details (for debugging)
              </summary>
              <div className="mt-3 space-y-2">
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Error</div>
                  <pre className="text-[11px] text-red-700 bg-red-50 rounded p-2 whitespace-pre-wrap break-words">{errorMessage}</pre>
                </div>
                {errorStack && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Stack</div>
                    <pre className="text-[10px] text-slate-700 bg-white rounded p-2 whitespace-pre-wrap break-words max-h-48 overflow-auto">{errorStack}</pre>
                  </div>
                )}
                {componentStack && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Component Stack</div>
                    <pre className="text-[10px] text-slate-600 bg-white rounded p-2 whitespace-pre-wrap break-words max-h-48 overflow-auto">{componentStack}</pre>
                  </div>
                )}
              </div>
            </details>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ===== Network Status Banner =====
export function NetworkStatusBanner() {
  const [online, setOnline] = React.useState(navigator.onLine);
  const [showReconnected, setShowReconnected] = React.useState(false);

  React.useEffect(() => {
    const onOnline = () => {
      setOnline(true);
      setShowReconnected(true);
      setTimeout(() => setShowReconnected(false), 3000);
    };
    const onOffline = () => { setOnline(false); setShowReconnected(false); };

    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  if (!online) {
    return (
      <div className="fixed top-0 left-0 right-0 z-[9999] bg-red-600 text-white text-center py-2 text-sm flex items-center justify-center gap-2" data-testid="network-offline-banner">
        <WifiOff className="h-4 w-4" />
        <span>No internet connection — retrying automatically...</span>
      </div>
    );
  }

  if (showReconnected) {
    return (
      <div className="fixed top-0 left-0 right-0 z-[9999] bg-green-600 text-white text-center py-2 text-sm flex items-center justify-center gap-2" data-testid="network-reconnected-banner">
        <Wifi className="h-4 w-4" />
        <span>Connection restored</span>
      </div>
    );
  }

  return null;
}
